import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Axios from 'axios';
import 'url-search-params-polyfill'; // URLSearchParams
import { withRouter } from 'react-router';
import moment from 'moment';
import styled from 'styled-components';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import every from 'lodash/every';
import classNames from 'classnames';
import { signupUser, signinUser, signoutUser } from '../../actions/index';
import Colors from '../../styles/colors';
import {
  LoadingWrapper,
  FullContentWrapper,
  FullContentSection,
  FormWrapper,
} from '../../styles/styled';
import CreatePublisher from '../../components/create_publisher';
import { emailValidator } from '../../helpers/validators';
import { sendSimpleWelcomeEmail } from '../../helpers/sendEmails';
import getCurrency from '../../helpers/getCurrency';
import { OrangeSubmitButton } from '../../components/buttons/buttons';
import { signupCommon, isBannedAccount } from '../../shared_methods/common_auth';
import { setupInitialSoundcast } from '../../shared_methods/setupInitialSoundcast';
import { addSoundcastToUser } from '../../shared_methods/add_soundcast_to_user';
import {
  handleChargeState,
  preparingDemoSoundcast,
} from '../../shared_methods/common_publisher';
import Loading from '../../components/Loading';
import SignUpForm from './SignUpForm';
import { getPublisherPlan } from '../../../server/scripts/planTiers';
import { authManager, userManager, publisherManager, soundcastManager, basicManager } from '../../helpers/managers';
import { ALERTS } from '../../helpers/texts';

const {
  accessExpirationWithGracePeriod,
  canPublisherAccessFeature,
  isStaging,
} = require('../../../server/scripts/utils')();

const getParamCaseInsensitive = (params, paramName) => {
  let paramValue = params.get(paramName);
  if (isEmpty(paramValue)) {
    const paramNameLowerCase = paramName.toLowerCase();
    for (const pair of params.entries()) {
      if (pair[0].toLowerCase() === paramNameLowerCase) {
        paramValue = pair[1];
        break;
      }
    }
  }
  return paramValue;
};

class AppSignup extends Component {
  constructor(props) {
    super(props);
    const feedVerified = JSON.parse(localStorage.getItem('feedVerified'));
    this.state = {
      publisherID: `${moment().format('x')}p`,
      firstName: '',
      lastName: '',
      email: feedVerified ? feedVerified.publisherEmail : '',
      password: '',
      message: '',
      publisherName: '',
      promoCode: '',
      pic_url: 'https://soundwiseinc.s3.amazonaws.com/user_profile_pic_placeholder.png',
      soundcastID: null,
      soundcast: null,
      loading: true,
      loadingMsg: 'Loading Audio Program',
      isPublisherFormShown: false,
      sumTotal: '',
      indicator: true,
      accountCreated: false,
      invitesExceeded: false,
      error: '',
      chargeState: JSON.parse(localStorage.getItem('chargeState')),
      isReachFreePrivateLimit: false,
    };

    this.partnerModes = ['appsumo', 'stackcommerce', 'appsumo-starter'];
    this.adminModes = ['admin', 'monitoring', ...this.partnerModes];
    this.listenerSoundcastModes = ['soundcast_user'];
    this.listenerDirectSignupModes = ['listener'];
    this.signUpWithPassword = this.signUpWithPassword.bind(this);
    this.signUpAdmin = this.signUpAdmin.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.signupCallback = this.signupCallback.bind(this);
    this.continueAccess = this.continueAccess.bind(this);
    this.switchAccountAccess = this.switchAccountAccess.bind(this);
  }

  componentDidMount() {
    const { match, history } = this.props;
    const { location = {} } = history;
    const { state: locationState = {} } = location;

    // invalid mode
    if (!this.isValidModes()) {
      return history.push('/notfound');
    }

    const params = new URLSearchParams(location.search);
    const soundcastID = match.params.id || locationState.soundcastID;
    if (this.isListenerSoundcastMode()) {
      if (isEmpty(soundcastID)) {
        return history.push('/notfound');
      }

      return new Promise(async (resolve, reject) => {
        try {
          // obtain soundcast either from locationState or from firebase by soundcastID
          let { soundcast = {}, checked, sumTotal, listenerDetails = {}, indicator = true } = locationState;

          if (isEmpty(soundcast)) {
            soundcast = await soundcastManager.getById(soundcastID);
            if (isNull(soundcast)) {
              return history.push('/notfound');
            }
          }

          const isShown = await this.isShownSoundcastSignup({ ...locationState, soundcast });
          if (!isShown) {
            return history.push('/notfound');
          }

          // get the price plan by planId or the first price plan
          let planId = Number(getParamCaseInsensitive(params, 'planId')) || 1;
          planId = planId < 1 ? 1 : planId;
          const { prices = [] } = soundcast;
          checked = params.get('checked') || checked || planId - 1 || 0;
          if (checked >= prices.length) {
            checked = 0;
          }

          let isReachFreePrivateLimit = false;

          if (!sumTotal) {
            const pricing = prices[checked] || {};
            if (pricing.price !== 'free') {
              const currency = getCurrency(pricing);
              sumTotal = `Total today: ${currency} ${Number(pricing.price).toFixed(2)}`;
            } else {
              const isPrivate = soundcast.published && !soundcast.landingPage;
              // pull publisher data for get limit and count of private soundcasts subscribers
              if (isPrivate) {
                const { data } = await Axios.get(`/api/publishers/usage/${soundcast.publisherID}`);
                const { current, limits } = data;
                const findValueInObjByName = (obj, name) => {
                  return name in obj ? obj[name] : null;
                };
                const limit = findValueInObjByName(limits, 'ALLOWED_SOUNDCAST_PRIVATE_SUBSCRIBERS');
                if (limit && limit < Number.MAX_VALUE) {
                  const count = findValueInObjByName(current, 'private_subscribers');
                  if (count) {
                    isReachFreePrivateLimit = count >= limit;
                  }
                }
              }
            }
          }

          resolve({
            loading: false,
            soundcastID,
            soundcast,
            checked,
            sumTotal,
            indicator,
            isReachFreePrivateLimit,
            ...listenerDetails,
          });
        } catch (e) {
          reject(e);
        }
      })
        .then(state => {
          this.setState({ ...state });
        })
        .catch(error => {
          console.log('Error signup', error);
        });
    }

    if (this.isAdminMode() && this.hasPathParamsId()) {
      // check the publisher if able to accept invitation signups
      this.pullOutPublisherUsage();
    } else {
      // promo code should be for publishers only
      // and for specific source (like appsump)
      let promoCode = '';
      if (this.isSpecialSignupMode()) {
        const promo = params.get('promo');
        promoCode = promo ? promo : promoCode;
      }

      // set default publisher email when detected
      const { email: oemail = '' } = this.state;
      const nemail = params.get('email');
      const email = nemail ? nemail : oemail;

      this.setState({
        loading: false,
        promoCode,
        email,
      });
    }
  }

  async isShownSoundcastSignup({ soundcast, checked = 0, coupon = '', listenerDetails = {} }) {
    const soundcastPublisher = await publisherManager.getById(soundcast.publisherID);
    const soundcastPrice = soundcast.prices && soundcast.prices[0] && soundcast.prices[0].price;
    const isSoundcastPaid = soundcast.forSale && soundcastPrice && soundcastPrice !== 'free';
    const isPrivate = soundcast.published && !soundcast.landingPage;
    const { isPaymentSignUp = false } = listenerDetails || {};

    if (soundcast.published && isSoundcastPaid && !soundcastPublisher.plan) {
      return false; // #166 if the soundcast is public && paid && (publisher.plan == null), then redirect to 404.
    }

    if (isPaymentSignUp || isPrivate) {
      return true; // #166  if the soundcast is private, render the signup form page
    }

    // if a coupon is used and its 100% off, allow it
    // only for starter plans, referred by appsumo campaign
    if (!isEmpty(coupon)) {
      const { active = false, plan, referredBy = '' } = getPublisherPlan(soundcastPublisher);
      const isStarterPlan = plan.toLowerCase() === 'starter';
      const isReferredByAppsumo = referredBy === 'appsumo-starter';
      if (active && isStarterPlan && isReferredByAppsumo) {
        const couponData = soundcast.prices[checked].coupons.find(item => item.code === coupon);
        const isCoupon100Off = !isEmpty(couponData) ? Number(couponData.percentOff) === 100 : false;
        if (isCoupon100Off) return true;
      }
    }

    // If the soundcast is a free one, then page should be rendered always
    if (
      !soundcast.forSale ||
      (soundcastPrice && soundcastPrice === 'free') ||
      soundcastPrice === '0' ||
      soundcastPrice === 0
    ) {
      return true;
    }

    // What we need here is to take the soundcast id and
    // check if the soundcast is paid && if the publisher of the soundcast is on
    // (plus || pro || platinum) plan, then render the page.
    if (soundcast.forSale) {
      // basic, plus, pro, platinum - these plans have the feature 'NON_PAYMENT_ACCESS_LINK'
      return (
        canPublisherAccessFeature(soundcastPublisher, 'NON_PAYMENT_ACCESS_LINK') &&
        soundcastPublisher.current_period_end > Math.floor(Date.now() / 1000)
      );
    }

    // Otherwise redirect to 404.
    return false;
  }

  continueAccess() {
    if (this.isListenerSoundcastMode()) {
      const { soundcastID, sumTotal, checked, soundcast } = this.state;
      const { userInfo, history } = this.props;
      const totalPrice = has(soundcast, `prices.${checked}.price`)
        ? Number(soundcast.prices[checked].price)
        : 0;

      addSoundcastToUser(
        null,
        userInfo,
        soundcastID,
        soundcast,
        checked,
        sumTotal,
        null,
        totalPrice,
        history
      );
    }
  }

  switchAccountAccess() {
    if (confirm('Are you sure you want to log out?')) {
      authManager.signOut().then(() => {
        this.props.signoutUser(false);
        window.location.reload();
      });
    }
  }

  showError(error) {
    this.setState({
      error,
      loading: false,
    });
  }

  async pullOutPublisherUsage() {
    const { match } = this.props;
    const publisherId = match.params.id;
    try {
      await this.showIndicator('Loading publisher', true, true);
      const { data } = await Axios.get(`/api/publishers/usage/${publisherId}`);
      const { current, limits } = data;
      const currentAdminCount = current.admins || 0;
      const currentSupportAdminCount = current.support_admins || 0;
      const signupExceedBypass = currentSupportAdminCount === 0 && this.isMonitoringSignupMode();
      const allowedAdminCount = limits.ALLOWED_ADMINS || 1;
      const invitesExceeded = currentAdminCount >= allowedAdminCount;
      if (!signupExceedBypass && invitesExceeded) {
        return this.setState({ invitesExceeded }, () =>
          this.showError(ALERTS().ALLOWED_ADMINS.SIGNUP)
        );
      }

      this.setState({ publisherID: publisherId });
    } catch (err) {
      console.error(err.message);
      this.showError('There was a problem loading the publisher details.');
    } finally {
      this.setState({ loading: false });
    }
  }

  async signUpWithPassword() {
    const { firstName, lastName, email, password, promoCode, invitesExceeded } = this.state;

    if (!this._validateForm(firstName, lastName, email, password)) {
      return false;
    }

    // for special signup mode, check for promo
    if (this.isSpecialSignupMode()) {
      if (isEmpty(promoCode)) {
        const currentMode = this.getCurrentMode();
        return this.showError(`Please enter your ${currentMode} code`);
      }
    }

    try {
      await this.showIndicator('Verifying', true, false);

      // if account already exist, ignore
      const accountExist = await this.verifyAccountExistence(email);
      if (accountExist) {
        return await this.hideIndicator();
      }

      // listener, show the form
      if (this.isListenerSoundcastMode()) {
        return await this._signUp(false);
      }

      // listener direct signup
      if (this.isListenerDirectSignupMode()) {
        return await this._signUp(false);
      }

      // admin from invitation with publisher id
      if (this.isAdminMode() && this.hasPathParamsId()) {
        if (invitesExceeded) {
          throw new Error(ALERTS().ALLOWED_ADMINS.SIGNUP);
        }

        return this.signUpInvitedAdmin();
      }

      // from here its just a solo admin signup
      // for special signup mode, verify promo
      if (this.isSpecialSignupMode()) {
        const validCode = await this.verifyPromocode(promoCode);
        if (!validCode) {
          return this.hideIndicator();
        }
      }

      // admin case, show the form asking for publisher name
      await this.hideIndicator();
      this.setState({ isPublisherFormShown: true });
    } catch (err) {
      console.log(err);
      this.showError(err.message);
      await this.hideIndicator();
    }
  }

  async signUpInvitedAdmin() {
    const { publisherID } = this.state;
    await this._signUp(false, false);
    const { uid: userId } = authManager.getCurrentUser() || {};
    if (!isEmpty(userId)) {
      try {
        await userManager.setInvitedAdmin(userId, publisherID);
        window.location.replace('/dashboard/soundcasts');
      } catch (err) {
        console.error(err.message);
        this.showError('There was a problem inviting the user as admin.');
      }
    }
  }

  async signUpAdmin() {
    const {
      publisherID,
      firstName,
      lastName,
      email,
      password,
      publisherName,
      promoCode,
      chargeState,
    } = this.state;

    if (isEmpty(publisherName)) {
      return this.showError('Please enter a Publisher name');
    }

    if (!this._validateForm(firstName, lastName, email, password)) return;

    await this.showIndicator('Processing', true, false);
    const userInfo = await this._signUp();
    const hasCharge = !isEmpty(chargeState);

    // admin case
    const { history } = this.props;
    const { location = {} } = history;
    const user = authManager.getCurrentUser();
    if (!user) return;
    try {
      // for first time publishers set a 2 weeks trial access in PLUS plan
      // this will get replace in dashboard anyway if they purchased a plan
      // only apply it to non special signup mode (like appsumo, stackcommerce, appsumo-starter)
      const periodEnd = moment().add(2, 'weeks').unix();
      const accessExpiration = accessExpirationWithGracePeriod(periodEnd);
      const trialAccess = !this.isSpecialSignupMode()
        // ? {
        //     plan: 'PLUS',
        //     auto_renewal: false,
        //     frequency: 'monthly',
        //     current_period_end: accessExpiration,
        //   }
        ? {
            //// 2022-06-21 .. we should not allow direct access unless credit info is already in the app state.
            plan: 'FREE',
            auto_renewal: false,
            current_period_end: moment().add(7, 'days').unix(),
          }
        : {};

      // #701 new publishers since 09/24/2020 will be considered as class 2
      // old publishers stays at class 1. For plan tier updates
      const publisherClass = 2;
      const nameEncoded = encodeURIComponent(publisherName);
      const imageUrl = `https://dummyimage.com/300.png/F76B1C/ffffff&text=${nameEncoded}`;
      const owner = user.uid;
      const now = moment();

      await publisherManager.save(publisherID, {
        name: publisherName,
        email,
        owner,
        imageUrl,
        administrators: { [owner]: true },
        class: publisherClass,
        created: now.unix(),
        transition: {
          episodes: 'done',
        },
        ...trialAccess,
      });

      const currentDate = now.utc().format();

      const partner = this.getCurrentMode();
      await Axios.post('/api/publishers', {
        publisherId: publisherID,
        name: publisherName,
        imageUrl,
        email,
        owner,
        class: publisherClass,
        hasCharge,
        referredBy: this.isSpecialSignupMode() ? partner : '',
        createdAt: currentDate,
        updatedAt: currentDate,
      });

      // for special signups with promocodes add it
      if (this.isSpecialSignupMode()) {
        await Axios.post(`/api/publishers/partner/${partner}`, {
          userId: user.uid,
          publisherId: publisherID,
          publisherEmail: email,
          promoCode,
        });
      }

      // create the demo soundcast here
      this.showIndicator('Preparing Dashboard', true, true);
      const errorMessage = await preparingDemoSoundcast(publisherID);
      if (!isEmpty(errorMessage)) {
        this.showIndicator('WARNING: An error occurred while creating the demo soundcast', true, true);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } catch (err) {
      console.error('Error adding publisher', err.message);
      await this.hideIndicator();
    } finally {
      await new Promise(resolve => setTimeout(resolve, 3000));

      // if there's a chargeState, update publisher details
      if (hasCharge) {
        try {
          this.showIndicator('Finalizing Account', true, true);
          await handleChargeState(userInfo, chargeState, publisherName);
          localStorage.removeItem('chargeState');
        } catch (err) {
          return alert(
            'There was a problem finalizing your publisher account, please notify support.'
          );
        }
      } else {
        if (!isEmpty(user.uid)) {
          const creatorID = user.uid;
          await basicManager.update({
            // [`users/${creatorID}/id`]: creatorID,
            [`users/${creatorID}/admin`]: true,
            // [`users/${creatorID}/publisherID`]: publisherID,
            [`users/${creatorID}/withTour`]: true,
          });
        }
      }

      // send welcome email to publisher
      sendSimpleWelcomeEmail({ firstName, lastName, email });

      this.showIndicator('Publisher account successfully created...', true, true);
      await new Promise(resolve => setTimeout(resolve, 2000));

      // redirect to specific location
      const params = new URLSearchParams(location.search);
      if (params.get('frequency') && params.get('plan')) {
        return history.push({
          pathname: '/buy',
          state: {
            plan: params.get('plan'),
            frequency: params.get('frequency'),
            price: params.get('price'),
          },
        });
      }

      window.location.replace('/dashboard/soundcasts');
    }
  }

  verifyPromocode(code) {
    return new Promise(async resolve => {
      // in case we support more source
      const partner = this.props.match.params.mode;
      Axios.get(`/api/promocodes/verify/${partner}/${code}`)
        .then(response => {
          const {
            data: { success, message },
          } = response;
          if (!success && !isEmpty(message)) {
            this.showError(message);
          }

          resolve(success);
        })
        .catch(error => {
          if ('response' in error) {
            const {
              data: { success, message },
            } = error.response;
            if (!success && !isEmpty(message)) {
              this.showError(message);
            }
          }

          resolve(false);
        });
    });
  }

  verifyAccountExistence(email) {
    return new Promise(resolve => {
      const { match, history } = this.props;
      const { soundcastID, soundcast, checked, sumTotal } = this.state;

      authManager
        .fetchSignInMethodsForEmail(email)
        .then(authProvider => {
          if (!isEmpty(authProvider)) {
            const text = 'This account already exists. Please sign in instead';
            if (this.isAdminMode() && this.hasPathParamsId()) {
              history.push(`/signin/admin/${match.params.id}`, { text });
            } else if (this.isListenerSoundcastMode() && soundcast) {
              history.push('/signin', {
                text,
                soundcastID,
                soundcast,
                checked,
                sumTotal,
                soundcastUser: true,
              });
            } else {
              history.push('/signin', { text });
            }

            return resolve(true);
          }

          return resolve(false);
        })
        .catch(err => {
          console.log(`Error: _signUp authProvider catch`, err);
          resolve(false);
        });
    });
  }

  _validateForm(firstName, lastName, email, password) {
    if ((firstName || '').length < 1 || (lastName || '').length < 1) {
      this.showError('Please complete your name');
      return false;
    } else if (!emailValidator(email)) {
      this.showError('Please enter a valid email address');
      return false;
    } else if (password.length < 6) {
      this.showError('Please enter a valid password');
      return false;
    }

    // bandaid solution to prevent certain emails to signup
    if (isBannedAccount(email)) {
      return false;
    }

    return true;
  }

  _signUp(fetchProvider = true, withTour = true) {
    return new Promise(async (resolve, reject) => {
      const { publisherID, email } = this.state;

      try {
        if (fetchProvider) {
          const accountExist = await this.verifyAccountExistence(email);
          if (accountExist) {
            return resolve();
          }
        }

        const npublisherId = this.isAdminMode() ? publisherID : null;
        const resultUser = await signupCommon(this.state, {
          publisherId: npublisherId,
          isListenerDirectSignup: this.isListenerDirectSignupMode(),
          withTour,
        });

        this.setState({ accountCreated: true }, () => {
          this.signupCallback(resultUser);
          resolve(resultUser);
        });
      } catch (err) {
        console.log(`Error: _signUp catch`, err);
        return reject(err);
      }
    });
  }

  handleInputChange(prop) {
    return e => {
      if (e.target.value) {
        const targetProps = {
          firstName: 'soundwiseSignupFName',
          lastName: 'soundwiseSignupLName',
          email: 'soundwiseSignupEmail',
        };

        // cache values (bug #58)
        const targetStorageKey = targetProps[prop];
        localStorage.setItem(targetStorageKey, e.target.value);
      }

      this.setState({ [prop]: e.target.value, error: '' });
    };
  }

  async signupCallback(user) {
    const { signupUser, history } = this.props;
    const { soundcastID, soundcast, checked, sumTotal } = this.state;
    signupUser(user);
    // for user -> goTo myPrograms, for admin need to register publisher first
    if (this.isListenerDirectSignupMode()) {
      history.push('/mysoundcasts');
    } else if (this.isListenerSoundcastMode()) {
      const initialSoundcast = { soundcastID, soundcast, checked, sumTotal, userInfo: user };
      // console.log('signupCallback', initialSoundcast);
      setupInitialSoundcast({
        ...initialSoundcast,
        success: state => {
          history.push({ pathname: '/notice', state });
        },
      });
    }
  }

  isValidModes() {
    const { match } = this.props;
    if (!this.hasPathParamsMode()) {
      return false;
    }

    return [
      ...this.adminModes,
      ...this.listenerSoundcastModes,
      ...this.listenerDirectSignupModes,
    ].includes(match.params.mode);
  }

  isAdminMode() {
    return this.isCurrentMode(this.adminModes);
  }

  isListenerSoundcastMode() {
    return this.isCurrentMode(this.listenerSoundcastModes);
  }

  isListenerDirectSignupMode() {
    return this.isCurrentMode(this.listenerDirectSignupModes);
  }

  isCurrentMode(modes) {
    const { match } = this.props;
    if (!this.hasPathParamsMode()) {
      return false;
    }

    return modes.includes(match.params.mode);
  }

  getCurrentMode() {
    const { match } = this.props;
    return match.params.mode;
  }

  isSpecialSignupMode() {
    const modes = [...this.partnerModes];
    const { match } = this.props;
    if (!this.hasPathParamsMode()) {
      return false;
    }

    // appsumo, stackcommerce, appsumo-starter is a special signup mode
    const nmodes = !isArray(modes) ? [modes] : modes;
    return nmodes.includes(match.params.mode);
  }

  isMonitoringSignupMode() {
    const { match } = this.props;
    return this.isAdminMode() && match.params.mode === 'monitoring';
  }

  hasPathParamsMode() {
    const { match } = this.props;
    return has(match, 'params.mode');
  }

  hasPathParamsId() {
    const { match } = this.props;
    return every(
      [
        has(match, 'params.id'),
        !this.isSpecialSignupMode(), // special signup mode don't have ids
      ],
      Boolean
    );
  }

  showIndicator(loadingMsg = 'Signing up', force = false, fullWindow = true) {
    return new Promise(resolve => {
      if (!this.state.indicator && !force) {
        return resolve();
      }

      this.setState(
        {
          indicator: fullWindow,
          loading: true,
          loadingMsg,
        },
        resolve
      );
    });
  }

  hideIndicator() {
    return new Promise(resolve => {
      if (!this.state.loading) {
        return resolve();
      }

      this.setState({ loading: false }, resolve);
    });
  }

  renderImageUrlNode() {
    const { soundcast } = this.state;
    const isForListener = this.isListenerSoundcastMode() && !isEmpty(soundcast);
    const alt = isForListener ? 'Soundcast Cover Art' : 'Soundwise Logo';
    const src = isForListener ? soundcast.imageURL : '/images/soundwiselogo.svg';

    return <img alt={alt} src={src} style={{ margin: '10px 0' }} />;
  }

  renderFormBody() {
    const { history } = this.props;
    const {
      firstName,
      lastName,
      email,
      emailReadOnly = false,
      password,
      soundcastID,
      publisherID,
      soundcast,
      checked,
      sumTotal,
      isPublisherFormShown,
      publisherName,
      promoCode,
      loading,
      accountCreated,
      error,
      invitesExceeded,
      isReachFreePrivateLimit,
      isPaymentSignUp = false,
    } = this.state;

    if(!isEmpty(soundcast)) {
      const { prices = [], disableSignup = false } = soundcast;
      const pricing = prices[checked] || {};
      if (pricing.price !== 'free' && (!isPaymentSignUp && disableSignup)) {
        return history.push('/notfound');
      }
    }

    // if there's a logged in user for listener mode signup
    const user = authManager.getCurrentUser();
    if (!isEmpty(user) && !accountCreated) {
      const { email } = user;
      const prompt =
        this.isAdminMode() && this.hasPathParamsId()
          ? `You were invited as an admin of another publisher but you're currently signed-in with email (${email}). Please log out of your account first.`
          : `You're currently signed-in with email (${email})`;

      const btnLabel = this.isAdminMode() ? 'LOGOUT' : 'PROCEED';
      const btnAccessHandler = this.isAdminMode() ? this.switchAccountAccess : this.continueAccess;

      if (isReachFreePrivateLimit) {
        return (
          <CurrentUserBlock>
            <div className="user-details text-large sm-text-large">
              <span>
                Subscription quota for private soundcasts has been filled for this publisher.
              </span>
            </div>
          </CurrentUserBlock>
        );
      }

      return (
        <CurrentUserBlock>
          <div className="user-details text-large sm-text-large">
            <span>{prompt}</span>
            <OrangeSubmitButton
              label={btnLabel}
              onClick={btnAccessHandler}
              styles={{ marginTop: 15, marginBottom: 10 }}
            />
            {!this.isAdminMode() && (
              <span className="switch" onClick={this.switchAccountAccess}>
                Logout
              </span>
            )}
          </div>
        </CurrentUserBlock>
      );
    }

    if (this.isAdminMode() && isPublisherFormShown) {
      return (
        <CreatePublisher
          onNameChange={this.handleInputChange('publisherName')}
          name={publisherName}
          onCreate={this.signUpAdmin}
          loading={loading}
        />
      );
    }

    const signInState = this.isListenerSoundcastMode()
      ? {
          soundcastID,
          soundcast,
          checked,
          sumTotal,
          soundcastUser: this.isListenerSoundcastMode(),
        }
      : {};

    // get the right sign in link on the form
    const inviteAdminMode = this.isAdminMode() && this.hasPathParamsId();
    const signInLink = inviteAdminMode ? `/signin/admin/${publisherID}` : '/signin';

    // if state coming from signin page using fb link show a warning
    const routeState = (history.location && history.location.state) || {};
    const { fromFBSignIn = false } = routeState;

    // disable the form if its invite admin mode and exceeded
    const disabled = inviteAdminMode && invitesExceeded;
    console.log('inviteAdminMode', inviteAdminMode);
    console.log('invitesExceeded', invitesExceeded);

    return (
      <SignUpForm
        firstName={firstName}
        lastName={lastName}
        email={email}
        emailReadOnly={emailReadOnly}
        password={password}
        promoCode={promoCode}
        onSignUp={this.signUpWithPassword}
        onInputChange={this.handleInputChange}
        processing={loading}
        error={error}
        modes={{
          admin: this.isAdminMode(),
          listener: this.isListenerSoundcastMode(),
          listenerDirectSignup: this.isListenerDirectSignupMode(),
          special: this.isSpecialSignupMode(),
        }}
        fromFBSignIn={fromFBSignIn}
        signInState={signInState}
        signInLink={signInLink}
        disabled={disabled}
      />
    );
  }

  renderSoundcastColumn = () => {
    const { soundcast } = this.state;

    if (!this.isListenerSoundcastMode() || isEmpty(soundcast)) {
      return null;
    }

    const { features = [] } = soundcast;

    return (
      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
        <div className="padding-five">
          <div
            className="margin-twenty-one-top sm-margin-nineteen-top title-medium text-dark-gray"
            style={{ paddingBottom: 35, textAlign: 'center' }}>
            <span>{soundcast.title}</span>
          </div>
          <div
            style={{ marginBottom: 20, fontSize: 15 }}
            className="text-large text-center text-dark-gray">
            {soundcast.short_description}
          </div>
          <ul className="" style={{ paddingBottom: '1em', display: 'flex', flexWrap: 'wrap' }}>
            {features.map((feature, i) => {
              if (isEmpty(feature)) {
                return null;
              }

              return (
                <li
                  key={i}
                  className=" text-dark-gray text-large  margin-lr-auto col-md-12 col-sm-12 col-xs-12 tz-text"
                  style={{
                    paddingLeft: '0em',
                    paddingRight: '1em',
                    paddingTop: '1em',
                    paddingBottom: '1em',
                    listStyleType: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <span style={{ paddingRight: 10 }}>⭐</span>
                  {feature}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  render() {
    const { indicator, loading, loadingMsg } = this.state;
    if (loading && indicator) {
      return (
        <LoadingWrapper>
          <Loading text={loadingMsg} align="center" size={50} fontSize={1.8} />
        </LoadingWrapper>
      );
    }

    const wrapperColumnClass = classNames('col-lg-6 col-md-6 col-sm-12 text-center', {
      'center-col': this.isAdminMode() || this.isListenerDirectSignupMode(),
    });

    return (
      <FullContentWrapper>
        <FullContentSection className="row" background={Colors.window}>
          <div className="col-lg-8 col-md-12 col-sm-12 col-xs-12 center-col">
            <div className="row">
              <div className={wrapperColumnClass}>
                <SignUpWindow className="row margin-lr-auto">
                  <div className="col-lg-6 col-md-6 col-xs-12 center-col margin-two-top margin-two-bottom">
                    {this.renderImageUrlNode()}
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-ten-bottom">
                    {this.renderFormBody()}
                  </div>
                </SignUpWindow>
              </div>
              {this.renderSoundcastColumn()}
            </div>
          </div>
        </FullContentSection>
      </FullContentWrapper>
    );
  }
}

const SignUpWindow = styled.div`
  max-width: 600px;
  min-width: 520px;

  @media (max-width: 767px) {
    min-width: initial;
  }
`;

const CurrentUserBlock = styled(FormWrapper)`
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .switch {
    display: block;
    cursor: pointer;
    color: ${Colors.mainOrange};
  }

  @media (max-width: 991px) {
    border-radius: 0 !important;
  }
`;

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ signupUser, signinUser, signoutUser }, dispatch);
}

const mapStateToProps = state => {
  const { userInfo, isLoggedIn, defaultSoundcastAdded } = state.user;
  return {
    userInfo,
    isLoggedIn,
    defaultSoundcastAdded,
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppSignup));
